import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import Icons from 'app/src/kit/Icons'
import Text from 'app/src/kit/Text'
import { RecentlyOpenedElement, useToggleFeature } from 'app/state'
import { isNil } from 'lodash-es'
import { SearchSelectableElement } from './SearchSelectableElement'

export const SearchResult: FC<
  RecentlyOpenedElement & {
    onPress?: () => void
    /**
     * When provided, prevents the default `<a>` navigation and calls this
     * instead — use for client-side routing that needs to preserve sub-paths.
     */
    onNavigate?: () => void
    /**
     * @default false
     */
    hideActionLabel?: boolean
  }
> = ({
  isFavorite,
  id,
  name,
  reverseGeocoding,
  organizationId,
  kind,
  onPress,
  technicalName,
  isVirtual,
  onNavigate,
  hideActionLabel
}) => {
  const { t } = useIntl()
  const { id: orgId } = useOrgContext()
  const [{ on_nav_favorites }] = useToggleFeature()

  const isAPlot = kind === 'plot'
  const isADevice = kind === 'device'

  const Icon = isAPlot
    ? Icons.PlotsFilled
    : isVirtual
      ? Icons.SingleVirtualSensorFilled
      : Icons.SingleSensorFilled

  const href = isADevice
    ? `/weather-map/${id}`
    : `/farms/${organizationId ?? orgId}/${isAPlot ? 'plots' : 'stations'}/${id}`

  return (
    <a
      href={href}
      onClick={(e) => {
        if (!isNil(onNavigate)) {
          e.preventDefault()
          onNavigate()
        }
        onPress?.()
      }}
    >
      <SearchSelectableElement>
        <Flex $alignItems='center' $gap='md' $minWidth={0}>
          <Icon $size='md' $color={'grayscale.black'} />
          <Flex $gap='md' $alignItems='baseline' $minWidth={0}>
            <Text $fontWeight='bold' $color={'grayscale.black'} $ellipsis>
              {name}
            </Text>
            <Text $color={'grayscale.700'} $fontWeight='medium' $ellipsis>
              {reverseGeocoding ?? t('models.device.unknown_location', { capitalize: true })}
            </Text>
            {!isNil(technicalName) ? (
              <Text
                $color={'grayscale.500'}
                $fontSize='sm'
                $fontVariantNumeric='tabular-nums'
                $ellipsis
              >
                {technicalName}
              </Text>
            ) : null}
          </Flex>
          {isFavorite && !on_nav_favorites ? (
            <Icons.Star $color={'primary.500'} $size='md' />
          ) : null}
        </Flex>
        <Flex $alignItems='center' $gap={'md'}>
          {hideActionLabel ? null : (
            <Text $color='grayscale.700' $fontWeight='semiBold' $lineHeight={undefined}>
              {t('actions.open')}
            </Text>
          )}
          <Icons.ArrowRight $size='lg' $color='grayscale.700' />
        </Flex>
      </SearchSelectableElement>
    </a>
  )
}
